import {
  RecordCategory,
  RecordUnit,
  Gender,
  Agent,
  ContentType,
  EFitnessProvider,
  ResponseLengthPreset,
  TonePreset,
  DocumentStatus,
  FeedbackCategory,
  SourcesFormat,
  Role,
  RoleWOSuperAdmin,
  ThreadCategory,
  STTProvider,
  ChatPlacementPosition,
} from '@form-factory/schema/enums';
import type { SupportedLanguageCode } from '@form-factory/shared';
import { t } from '@lingui/core/macro';

/**
 * This type contains all the translations for the enums
 * that can be reused in the admin app.
 *
 * It is used mainly to ensure type safetry on the
 * returned translations from `getEnumTranslations()`.
 */
export type EnumTranslations = {
  tonePreset: Record<TonePreset, string>;
  agent: Record<Agent, string>;
  responseLengthPreset: Record<ResponseLengthPreset, string>;
  contentType: Record<ContentType, string>;
  supportedLanguages: Record<SupportedLanguageCode, string>;
  gender: Record<Gender, string>;
  efitnessProvider: Record<EFitnessProvider, string>;
  documentStatus: Record<DocumentStatus, string>;
  feedbackCategory: Record<FeedbackCategory, string>;
  sourcesFormat: Record<SourcesFormat, string>;
  role: Record<Role, string>;
  recordUnit: Record<RecordUnit, string>;
  threadCategory: Record<ThreadCategory, string>;
  roleWOSuperAdmin: Record<RoleWOSuperAdmin, string>;
  sttProvider: Record<STTProvider, string>;
  recordCategory: Record<RecordCategory, string>;
  chatPlacementPosition: Record<ChatPlacementPosition, string>;
};

/**
 * This function contains translations for enums and other constants
 * that can be reused in the admin app. It is memoized to avoid
 * re-rendering the same translations.
 */
export function getEnumTranslations() {
  return {
    tonePreset: {
      [TonePreset.Friendly]: t`Friendly`,
      [TonePreset.Casual]: t`Casual`,
      [TonePreset.Balanced]: t`Balanced`,
      [TonePreset.Professional]: t`Professional`,
      [TonePreset.Formal]: t`Formal`,
    },
    threadCategory: {
      [ThreadCategory.Chat]: t`Chat`,
    },
    efitnessProvider: {
      [EFitnessProvider.Croatia]: t`Croatia`,
      [EFitnessProvider.Slovakia]: t`Slovakia`,
      [EFitnessProvider.Czechia]: t`Czechia`,
    },
    chatPlacementPosition: {
      [ChatPlacementPosition.Left]: t`Left`,
      [ChatPlacementPosition.Right]: t`Right`,
    },
    recordUnit: {
      [RecordUnit.Tokens]: t`Tokens`,
      [RecordUnit.Characters]: t`Characters`,
      [RecordUnit.Seconds]: t`Seconds`,
      [RecordUnit.Credits]: t`Credits`,
    },
    recordCategory: {
      [RecordCategory.Agent]: t`Agent`,
      [RecordCategory.Data]: t`Data`,
      [RecordCategory.Other]: t`Other`,
      [RecordCategory.RAG]: t`RAG`,
    },
    gender: {
      [Gender.None]: t`None`,
      [Gender.Male]: t`Male`,
      [Gender.Female]: t`Female`,
    },
    feedbackCategory: {
      [FeedbackCategory.InsufficientDetail]: t`Insufficient detail`,
      [FeedbackCategory.IrrelevantInformation]: t`Irrelevant information`,
      [FeedbackCategory.MissingInformation]: t`Missing information`,
      [FeedbackCategory.IncorrectInformation]: t`Incorrect information`,
      [FeedbackCategory.Other]: t`Other`,
    },
    agent: {
      [Agent.FormFactory]: t`Form Factory`,
    },
    contentType: {
      [ContentType.ExternalDocument]: t`External document`,
      [ContentType.Website]: t`Website`,
      [ContentType.Document]: t`Document`,
    },
    sourcesFormat: {
      [SourcesFormat.Simple]: t`Simple`,
      [SourcesFormat.Academic]: t`Academic`,
    },
    responseLengthPreset: {
      [ResponseLengthPreset.VeryShort]: t`Very short`,
      [ResponseLengthPreset.Short]: t`Short`,
      [ResponseLengthPreset.Medium]: t`Medium`,
      [ResponseLengthPreset.Long]: t`Long`,
      [ResponseLengthPreset.VeryLong]: t`Very long`,
    },
    supportedLanguages: {
      en: t`English`,
      de: t`German`,
      fr: t`French`,
      es: t`Spanish`,
      it: t`Italian`,
      nl: t`Dutch`,
      pl: t`Polish`,
      pt: t`Portuguese`,
      da: t`Danish`,
      fi: t`Finnish`,
      no: t`Norwegian`,
      sv: t`Swedish`,
      is: t`Icelandic`,
      ru: t`Russian`,
      uk: t`Ukrainian`,
      cs: t`Czech`,
      sk: t`Slovak`,
      hu: t`Hungarian`,
      bg: t`Bulgarian`,
      ro: t`Romanian`,
      sr: t`Serbian`,
      hr: t`Croatian`,
      mk: t`Macedonian`,
      sl: t`Slovenian`,
      sq: t`Albanian`,
      lt: t`Lithuanian`,
      lv: t`Latvian`,
      et: t`Estonian`,
      el: t`Greek`,
      tr: t`Turkish`,
      he: t`Hebrew`,
      ca: t`Catalan`,
      gd: t`Scottish Gaelic`,
      cy: t`Welsh`,
      ga: t`Irish`,
      bs: t`Bosnian`,
      be: t`Belarusian`,
      ka: t`Georgian`,
      hy: t`Armenian`,
    },
    documentStatus: {
      [DocumentStatus.ParsingError]: t`Parsing error`,
      [DocumentStatus.Uploading]: t`Uploading`,
      [DocumentStatus.Ready]: t`Ready`,
      [DocumentStatus.Parsing]: t`Parsing`,
      [DocumentStatus.ParsingAsync]: t`Parsing (async)`,
      [DocumentStatus.EmbeddingError]: t`Embedding error`,
      [DocumentStatus.Parsed]: t`Parsed`,
      [DocumentStatus.Embedding]: t`Embedding`,
      [DocumentStatus.Done]: t`Done`,
    },
    role: {
      [Role.SuperAdmin]: t`Super admin`,
      [Role.Admin]: t`Admin`,
    },
    roleWOSuperAdmin: {
      [RoleWOSuperAdmin.Admin]: t`Admin`,
    },
    sttProvider: {
      [STTProvider.Gladia]: t`Gladia`,
      [STTProvider.Deepgram]: t`Deepgram`,
    },
  } satisfies EnumTranslations;
}
